import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  X,
  MoreHorizontal,
  Pencil,
  Trash2,
  Copy,
  Star,
  GripVertical,
  ChevronUp,
  ChevronDown,
  FileText,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PageHeader, MetricCard, KPIStrip } from '@/components/common';
import {
  rateTemplatesApi,
  chargeTypesApi,
  RateTemplate,
  ChargeType,
  CreateRateTemplateDto,
  QueryRateTemplateParams,
  ShipmentType,
  ChargeCategoryExtended,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { stagger, fadeUp } from '@/lib/animations';

// Constants
const SHIPMENT_TYPES: { value: ShipmentType; label: string }[] = [
  { value: 'FTL', label: 'FTL' },
  { value: 'LTL', label: 'LTL' },
  { value: 'OCEAN_FCL', label: 'Ocean FCL' },
  { value: 'OCEAN_LCL', label: 'Ocean LCL' },
  { value: 'AIR', label: 'Air' },
  { value: 'RAIL', label: 'Rail' },
  { value: 'BREAK_BULK', label: 'Break Bulk' },
  { value: 'INTERMODAL', label: 'Intermodal' },
];

const CHARGE_CATEGORIES: { value: ChargeCategoryExtended; label: string }[] = [
  { value: 'Freight', label: 'Freight' },
  { value: 'OriginCharges', label: 'Origin Charges' },
  { value: 'DestinationCharges', label: 'Destination Charges' },
  { value: 'OriginHaulage', label: 'Origin Haulage' },
  { value: 'DestinationHaulage', label: 'Destination Haulage' },
  { value: 'ExportClearance', label: 'Export Clearance' },
  { value: 'ImportClearance', label: 'Import Clearance' },
  { value: 'Documentation', label: 'Documentation' },
  { value: 'Insurance', label: 'Insurance' },
];

const CALCULATION_TYPES = [
  { value: 'Fixed', label: 'Fixed' },
  { value: 'Percentage', label: 'Percentage' },
  { value: 'Variable', label: 'Variable' },
] as const;

const CATEGORY_COLORS: Record<ChargeCategoryExtended, string> = {
  Freight: 'bg-primary-50 text-primary-600',
  OriginCharges: 'bg-success-50 text-success-600',
  DestinationCharges: 'bg-purple-50 text-purple-600',
  OriginHaulage: 'bg-warning-50 text-warning-600',
  DestinationHaulage: 'bg-warning-50 text-warning-700',
  ExportClearance: 'bg-primary-50 text-primary-500',
  ImportClearance: 'bg-success-50 text-success-500',
  Documentation: 'bg-muted text-muted-foreground',
  Insurance: 'bg-danger-50 text-danger-500',
};

const TYPE_COLORS: Record<ShipmentType, string> = {
  FTL: 'bg-primary-50 text-primary-600',
  LTL: 'bg-primary-50 text-primary-500',
  OCEAN_FCL: 'bg-success-50 text-success-600',
  OCEAN_LCL: 'bg-success-50 text-success-500',
  AIR: 'bg-purple-50 text-purple-600',
  RAIL: 'bg-warning-50 text-warning-600',
  BREAK_BULK: 'bg-warning-50 text-warning-700',
  INTERMODAL: 'bg-muted text-muted-foreground',
};

// Charge form item type
interface ChargeFormItem {
  id: string;
  chargeCode: string;
  chargeName: string;
  category: ChargeCategoryExtended;
  calculationType: 'Fixed' | 'Percentage' | 'Variable';
  isRequired: boolean;
}

// Slab form item type
interface SlabFormItem {
  id: string;
  minWeight: number;
  maxWeight?: number;
  ratePerUnit?: number;
  unit: 'KG' | 'LB';
  minimumCharge?: number;
}

export default function RateTemplates() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<RateTemplate[]>([]);
  const [chargeTypes, setChargeTypes] = useState<ChargeType[]>([]);

  // Sheet/Dialog states
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RateTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<RateTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    shipmentType: ShipmentType;
    charges: ChargeFormItem[];
    slabs: SlabFormItem[];
  }>({
    name: '',
    description: '',
    shipmentType: 'FTL',
    charges: [],
    slabs: [],
  });

  // Parse filters from URL
  const activeTab = (searchParams.get('type') as ShipmentType) || 'FTL';
  const searchFilter = searchParams.get('search') || '';
  const showInactiveFilter = searchParams.get('showInactive') === 'true';

  const setFilter = (key: string, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  // Load data
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params: QueryRateTemplateParams = {
        shipmentType: activeTab,
        search: searchFilter || undefined,
        isActive: showInactiveFilter ? undefined : true,
      };
      const response = await rateTemplatesApi.getAll(params);
      setTemplates(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchFilter, showInactiveFilter]);

  const loadChargeTypes = useCallback(async () => {
    try {
      const response = await chargeTypesApi.getAll({ isActive: true });
      setChargeTypes(response.data);
    } catch (error) {
      console.error('Failed to load charge types:', error);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    loadChargeTypes();
  }, [loadChargeTypes]);

  // Computed stats
  const totalCount = templates.length;
  const defaultCount = useMemo(() => templates.filter((t) => t.isDefault).length, [templates]);
  const avgChargesCount = useMemo(() => {
    if (templates.length === 0) return 0;
    const total = templates.reduce((sum, t) => sum + (t.chargeStructure?.length || 0), 0);
    return Math.round(total / templates.length);
  }, [templates]);

  // Filter templates
  const filteredTemplates = useMemo(() => {
    let result = templates;
    if (searchFilter) {
      const lower = searchFilter.toLowerCase();
      result = result.filter(
        (t) => t.name.toLowerCase().includes(lower) || t.description?.toLowerCase().includes(lower)
      );
    }
    return result;
  }, [templates, searchFilter]);

  // Generate unique ID
  const generateId = () => Math.random().toString(36).substring(2, 11);

  // Handlers
  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      shipmentType: activeTab,
      charges: [],
      slabs: [],
    });
    setSheetOpen(true);
  };

  const handleEdit = (template: RateTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      shipmentType: template.shipmentType,
      charges: (template.chargeStructure || []).map((c) => ({
        id: generateId(),
        chargeCode: c.chargeCode,
        chargeName: c.chargeName,
        category: c.category,
        calculationType: c.calculationType,
        isRequired: c.isRequired,
      })),
      slabs: (template.slabStructure || []).map((s) => ({
        id: generateId(),
        minWeight: s.minWeight,
        maxWeight: s.maxWeight,
        ratePerUnit: s.ratePerUnit,
        unit: s.unit,
        minimumCharge: s.minimumCharge,
      })),
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.shipmentType) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.charges.length === 0) {
      toast.error('Please add at least one charge to the template');
      return;
    }

    setSaving(true);
    try {
      const dto: CreateRateTemplateDto = {
        name: formData.name,
        description: formData.description || undefined,
        shipmentType: formData.shipmentType,
        chargeStructure: formData.charges.map((c) => ({
          chargeCode: c.chargeCode,
          chargeName: c.chargeName,
          category: c.category,
          calculationType: c.calculationType,
          isRequired: c.isRequired,
        })),
        slabStructure:
          formData.shipmentType === 'AIR' && formData.slabs.length > 0
            ? formData.slabs.map((s) => ({
                minWeight: s.minWeight,
                maxWeight: s.maxWeight,
                ratePerUnit: s.ratePerUnit,
                unit: s.unit,
                minimumCharge: s.minimumCharge,
              }))
            : undefined,
      };

      if (editingTemplate) {
        await rateTemplatesApi.update(editingTemplate.id, dto);
        toast.success('Template updated successfully');
      } else {
        await rateTemplatesApi.create(dto);
        toast.success('Template created successfully');
      }
      setSheetOpen(false);
      await loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      await rateTemplatesApi.delete(templateToDelete.id);
      toast.success('Template deleted successfully');
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      await loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDuplicate = async (template: RateTemplate) => {
    try {
      await rateTemplatesApi.duplicate(template.id);
      toast.success('Template duplicated successfully');
      await loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSetDefault = async (template: RateTemplate) => {
    try {
      await rateTemplatesApi.setAsDefault(template.id);
      toast.success('Template set as default');
      await loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleActive = async (template: RateTemplate) => {
    try {
      await rateTemplatesApi.update(template.id, { isActive: !template.isActive });
      toast.success(`Template ${template.isActive ? 'deactivated' : 'activated'}`);
      await loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Charge management
  const addCharge = () => {
    setFormData({
      ...formData,
      charges: [
        ...formData.charges,
        {
          id: generateId(),
          chargeCode: '',
          chargeName: '',
          category: 'Freight',
          calculationType: 'Fixed',
          isRequired: false,
        },
      ],
    });
  };

  const updateCharge = (id: string, updates: Partial<ChargeFormItem>) => {
    setFormData({
      ...formData,
      charges: formData.charges.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    });
  };

  const removeCharge = (id: string) => {
    setFormData({
      ...formData,
      charges: formData.charges.filter((c) => c.id !== id),
    });
  };

  const moveCharge = (index: number, direction: 'up' | 'down') => {
    const newCharges = [...formData.charges];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newCharges.length) return;
    [newCharges[index], newCharges[newIndex]] = [newCharges[newIndex], newCharges[index]];
    setFormData({ ...formData, charges: newCharges });
  };

  const handleChargeTypeSelect = (id: string, chargeTypeId: string) => {
    const chargeType = chargeTypes.find((ct) => ct.id === chargeTypeId);
    if (chargeType) {
      updateCharge(id, {
        chargeCode: chargeType.code,
        chargeName: chargeType.name,
        category: chargeType.category,
      });
    }
  };

  // Slab management (for Air freight)
  const addSlab = () => {
    const lastSlab = formData.slabs[formData.slabs.length - 1];
    const minWeight = lastSlab ? (lastSlab.maxWeight || 0) + 1 : 0;
    setFormData({
      ...formData,
      slabs: [
        ...formData.slabs,
        {
          id: generateId(),
          minWeight,
          maxWeight: undefined,
          ratePerUnit: undefined,
          unit: 'KG',
          minimumCharge: undefined,
        },
      ],
    });
  };

  const updateSlab = (id: string, updates: Partial<SlabFormItem>) => {
    setFormData({
      ...formData,
      slabs: formData.slabs.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    });
  };

  const removeSlab = (id: string) => {
    setFormData({
      ...formData,
      slabs: formData.slabs.filter((s) => s.id !== id),
    });
  };

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Rate Templates"
          description="Reusable rate structures by shipment type, applied when a new rate card is created."
          actions={
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          }
        />
      </motion.div>

      {/* KPIs */}
      <div>
        <KPIStrip columns={4}>
          <MetricCard
            label="Total Templates"
            value={totalCount}
            icon={<FileText className="w-5 h-5" />}
            trend={{ direction: 'neutral', value: String(filteredTemplates.length), text: 'shown' }}
            sentiment="info"
          />
          <MetricCard
            label="Default Templates"
            value={defaultCount}
            icon={<Star className="w-5 h-5 text-warning-500" />}
            sentiment="warning"
          />
          <MetricCard
            label="Custom Templates"
            value={totalCount - defaultCount}
            icon={<FileText className="w-5 h-5 text-success-500" />}
            sentiment="success"
          />
          <MetricCard
            label="Avg Charges"
            value={avgChargesCount}
            icon={<FileText className="w-5 h-5 text-primary-500" />}
            trend={{ direction: 'neutral', value: 'per template' }}
            sentiment="neutral"
          />
        </KPIStrip>
      </div>

      {/* Tabs and Filters */}
      <motion.div variants={fadeUp}>
        <div className={cn(PANEL, 'p-4 space-y-4')}>
          <Tabs value={activeTab} onValueChange={(v) => setFilter('type', v)}>
            <TabsList className="flex flex-wrap">
              {SHIPMENT_TYPES.map((type) => (
                <TabsTrigger key={type.value} value={type.value}>
                  {type.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search templates..."
                value={searchFilter}
                onChange={(e) => setFilter('search', e.target.value || undefined)}
                className="pl-10 pr-10"
              />
              {searchFilter && (
                <button
                  onClick={() => setFilter('search', undefined)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Show Inactive */}
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={showInactiveFilter}
                onCheckedChange={(checked) =>
                  setFilter('showInactive', checked ? 'true' : undefined)
                }
              />
              Show Inactive
            </label>
          </div>
        </div>
      </motion.div>

      {/* Templates Grid */}
      <motion.div variants={fadeUp}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredTemplates.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className={`bg-card rounded-xl border p-4 space-y-4 ${
                  !template.isActive ? 'opacity-60' : ''
                }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold">{template.name}</h3>
                      {template.isDefault && (
                        <Badge className="bg-status-warning-subtle text-status-warning-foreground">
                          <Star className="w-3 h-3 mr-1" />
                          Default
                        </Badge>
                      )}
                      {!template.isActive && <Badge variant="secondary">Inactive</Badge>}
                    </div>
                    {template.description && (
                      <p className="text-sm text-muted-foreground">{template.description}</p>
                    )}
                    <Badge variant="outline" className={TYPE_COLORS[template.shipmentType]}>
                      {SHIPMENT_TYPES.find((t) => t.value === template.shipmentType)?.label ||
                        template.shipmentType}
                    </Badge>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label="More actions">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(template)}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      {!template.isDefault && (
                        <DropdownMenuItem onClick={() => handleSetDefault(template)}>
                          <Star className="w-4 h-4 mr-2" />
                          Set as Default
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleToggleActive(template)}>
                        {template.isActive ? (
                          <>
                            <X className="w-4 h-4 mr-2" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Activate
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          setTemplateToDelete(template);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Charge Structure */}
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">
                    Charge Structure ({template.chargeStructure?.length || 0} charges)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(template.chargeStructure || []).slice(0, 6).map((charge, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className={charge.category ? CATEGORY_COLORS[charge.category] : ''}
                      >
                        {charge.chargeName || charge.chargeCode}
                        {charge.isRequired && (
                          <span className="text-status-destructive ml-1">*</span>
                        )}
                      </Badge>
                    ))}
                    {(template.chargeStructure?.length || 0) > 6 && (
                      <Badge variant="secondary">
                        +{(template.chargeStructure?.length || 0) - 6} more
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Weight Slabs (for Air) */}
                {template.shipmentType === 'AIR' &&
                  template.slabStructure &&
                  template.slabStructure.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-muted-foreground">
                        Weight Slabs ({template.slabStructure.length})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {template.slabStructure.slice(0, 4).map((slab, idx) => (
                          <Badge key={idx} variant="outline">
                            {slab.minWeight}-{slab.maxWeight || '+'} {slab.unit}
                          </Badge>
                        ))}
                        {template.slabStructure.length > 4 && (
                          <Badge variant="secondary">
                            +{template.slabStructure.length - 4} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            ))}
          </div>
        ) : (
          <div className={cn(PANEL, 'p-12 text-center')}>
            <AlertTriangle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-base font-semibold mb-2">No Templates Found</h3>
            <p className="text-muted-foreground mb-4">
              {searchFilter
                ? 'No templates match your search.'
                : `No templates exist for ${SHIPMENT_TYPES.find((t) => t.value === activeTab)?.label || activeTab} shipments yet.`}
            </p>
            {!searchFilter && (
              <Button onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Template
              </Button>
            )}
          </div>
        )}
      </motion.div>

      {/* Create/Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingTemplate ? 'Edit Template' : 'New Rate Template'}</SheetTitle>
            <SheetDescription>
              {editingTemplate
                ? 'Update the rate template configuration.'
                : 'Create a new rate template for consistent pricing.'}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6 py-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Standard Ocean FCL Template"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shipmentType">Shipment Type *</Label>
                <Select
                  value={formData.shipmentType}
                  onValueChange={(v) =>
                    setFormData({ ...formData, shipmentType: v as ShipmentType })
                  }
                >
                  <SelectTrigger id="shipmentType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SHIPMENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Charge Structure */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Charge Structure *</Label>
                <Button variant="outline" size="sm" onClick={addCharge}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Charge
                </Button>
              </div>

              {formData.charges.length === 0 ? (
                <div className="border border-dashed rounded-lg p-4 text-center text-muted-foreground">
                  No charges added. Click "Add Charge" to start building the template.
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.charges.map((charge, index) => (
                    <div
                      key={charge.id}
                      className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg border"
                    >
                      <div className="flex flex-col gap-1 pt-2">
                        <button
                          type="button"
                          onClick={() => moveCharge(index, 'up')}
                          disabled={index === 0}
                          className="p-1 hover:bg-muted rounded disabled:opacity-30"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <GripVertical className="w-3 h-3 text-muted-foreground" />
                        <button
                          type="button"
                          onClick={() => moveCharge(index, 'down')}
                          disabled={index === formData.charges.length - 1}
                          className="p-1 hover:bg-muted rounded disabled:opacity-30"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>

                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div className="col-span-2">
                          <Select
                            value=""
                            onValueChange={(v) => handleChargeTypeSelect(charge.id, v)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select charge type..." />
                            </SelectTrigger>
                            <SelectContent>
                              {chargeTypes.map((ct) => (
                                <SelectItem key={ct.id} value={ct.id}>
                                  {ct.code} - {ct.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <Input
                          placeholder="Charge code"
                          value={charge.chargeCode}
                          onChange={(e) => updateCharge(charge.id, { chargeCode: e.target.value })}
                        />
                        <Input
                          placeholder="Charge name"
                          value={charge.chargeName}
                          onChange={(e) => updateCharge(charge.id, { chargeName: e.target.value })}
                        />

                        <div>
                          <Select
                            value={charge.category || ''}
                            onValueChange={(v) =>
                              updateCharge(charge.id, { category: v as ChargeCategoryExtended })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent>
                              {CHARGE_CATEGORIES.map((cat) => (
                                <SelectItem key={cat.value} value={cat.value}>
                                  {cat.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Select
                            value={charge.calculationType || ''}
                            onValueChange={(v) =>
                              updateCharge(charge.id, {
                                calculationType: v as 'Fixed' | 'Percentage' | 'Variable',
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Calc Type" />
                            </SelectTrigger>
                            <SelectContent>
                              {CALCULATION_TYPES.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="col-span-2 flex items-center gap-2">
                          <Checkbox
                            id={`required-${charge.id}`}
                            checked={charge.isRequired}
                            onCheckedChange={(checked) =>
                              updateCharge(charge.id, { isRequired: !!checked })
                            }
                          />
                          <Label htmlFor={`required-${charge.id}`} className="text-sm font-normal">
                            Required for all quotes
                          </Label>
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Remove"
                        className="shrink-0"
                        onClick={() => removeCharge(charge.id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Weight Slabs (for Air freight only) */}
            {formData.shipmentType === 'AIR' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Weight Slabs (Optional)</Label>
                  <Button variant="outline" size="sm" onClick={addSlab}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Slab
                  </Button>
                </div>

                {formData.slabs.length === 0 ? (
                  <div className="border border-dashed rounded-lg p-4 text-center text-muted-foreground text-sm">
                    Optional: Add weight slabs for tiered pricing on Air freight.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.slabs.map((slab) => (
                      <div key={slab.id} className="flex items-center gap-2">
                        <div className="flex-1 grid grid-cols-5 gap-2">
                          <Input
                            type="number"
                            placeholder="Min"
                            value={slab.minWeight}
                            onChange={(e) =>
                              updateSlab(slab.id, { minWeight: parseFloat(e.target.value) || 0 })
                            }
                          />
                          <Input
                            type="number"
                            placeholder="Max"
                            value={slab.maxWeight || ''}
                            onChange={(e) =>
                              updateSlab(slab.id, {
                                maxWeight: e.target.value ? parseFloat(e.target.value) : undefined,
                              })
                            }
                          />
                          <Select
                            value={slab.unit}
                            onValueChange={(v) => updateSlab(slab.id, { unit: v as 'KG' | 'LB' })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="KG">KG</SelectItem>
                              <SelectItem value="LB">LB</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            placeholder="Rate/unit"
                            value={slab.ratePerUnit || ''}
                            onChange={(e) =>
                              updateSlab(slab.id, {
                                ratePerUnit: e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              })
                            }
                          />
                          <Input
                            type="number"
                            placeholder="Min charge"
                            value={slab.minimumCharge || ''}
                            onChange={(e) =>
                              updateSlab(slab.id, {
                                minimumCharge: e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              })
                            }
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Remove"
                          onClick={() => removeSlab(slab.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-status-destructive-subtle rounded-full">
                <Trash2 className="w-5 h-5 text-status-destructive-foreground" />
              </div>
              Delete Template
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium">{templateToDelete?.name}</span>? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
